// LoginPage.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  TextField, 
  Button, 
  Container, 
  Typography, 
  Paper, 
  IconButton, 
  InputAdornment, 
  CircularProgress, 
  Box 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { detectDeviceType } from '../services/authService';
import { useTranslation } from 'react-i18next';
import { useUser } from '../contexts/UserContext';
import { useQueryClient } from '@tanstack/react-query';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

async function fetchClients() {
  const response = await API.get('/clients/');
  return response.data;
}

const LoginPage = () => {
  // Load saved username from localStorage
  const [credentials, setCredentials] = useState({ 
    username: localStorage.getItem('saved_username') || '', 
    password: '' 
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', severity: 'info' });
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { fetchProfile } = useUser();
  const queryClient = useQueryClient();

  // Refs for autofill detection
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  // Fix autofill label overlap
  useEffect(() => {
    // Check for autofilled values after a short delay
    const checkAutofill = setInterval(() => {
      const usernameInput = usernameRef.current?.querySelector('input');
      const passwordInput = passwordRef.current?.querySelector('input');
      
      if (usernameInput) {
        // Check if browser autofilled
        const isAutofilled = usernameInput.matches(':-webkit-autofill');
        const hasValue = usernameInput.value && usernameInput.value !== credentials.username;
        
        if (isAutofilled || hasValue) {
          setCredentials(prev => ({ ...prev, username: usernameInput.value }));
          localStorage.setItem('saved_username', usernameInput.value);
          clearInterval(checkAutofill);
        }
      }
      
      if (passwordInput) {
        const isAutofilled = passwordInput.matches(':-webkit-autofill');
        const hasValue = passwordInput.value && passwordInput.value !== credentials.password;
        
        if (isAutofilled || hasValue) {
          setCredentials(prev => ({ ...prev, password: passwordInput.value }));
        }
      }
    }, 100);

    // Stop checking after 2 seconds
    setTimeout(() => clearInterval(checkAutofill), 2000);
    
    return () => clearInterval(checkAutofill);
  }, []);

  // Countdown timer for rate limiting
  useEffect(() => {
    let timer;
    if (retryAfter > 0) {
      timer = setInterval(() => {
        setRetryAfter((prev) => {
          if (prev <= 1) {
            setRateLimited(false);
            setMessage({ text: '', severity: 'info' });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [retryAfter]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rateLimited) {
      return;
    }

    // Trim whitespace from username
    const trimmedUsername = credentials.username.trim();
    setCredentials(prev => ({ ...prev, username: trimmedUsername }));
    localStorage.setItem('saved_username', trimmedUsername);

    setLoading(true);
    setMessage({ text: '', severity: 'info' });

    try {
      const deviceType = detectDeviceType();
      const response = await API.post('/auth/login/', {
        ...credentials,
        username: trimmedUsername,
        device_type: deviceType,
      });
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
      localStorage.setItem('device_type', deviceType);
      const profile = await fetchProfile();

      // Determine redirect based on user's allowed pages
      const role = profile?.role;
      const pagesConfigs = profile?.ui_config?.pages_configs || {};

      const clientsAllowed = pagesConfigs.ClientsListPageCached?.allowed_roles?.includes(role) ?? true;
      const ordersAllowed = pagesConfigs.OrdersPage?.allowed_roles?.includes(role) ?? true;
      const productsAllowed = pagesConfigs.ProductsPage?.allowed_roles?.includes(role) ?? true;

      if (clientsAllowed) {
        navigate('/clients');
      } else if (ordersAllowed) {
        navigate('/orders');
      } else if (productsAllowed) {
        navigate('/products');
      } else {
        // Fallback to clients if no specific config
        navigate('/clients');
      }
    } catch (error) {
      // Clear only password, keep username
      setCredentials(prev => ({ ...prev, password: '' }));
      
      if (error.response) {
        if (error.response.status === 429) {
          // Rate limited
          setRateLimited(true);
          const retryTime = error.response.data.retry_after || 60;
          setRetryAfter(retryTime);
          setMessage({ 
            text: `יותר מדי ניסיונות התחברות. נסה שוב בעוד ${retryTime} שניות`, 
            severity: 'error' 
          });
        } else {
          setMessage({ text: 'שם משתמש או סיסמה שגויים', severity: 'error' });
        }
      } else {
        setMessage({ text: 'השירות אינו זמין כרגע, נסה שוב מאוחר יותר.', severity: 'warning' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setCredentials({...credentials, username: value});
    // Save username to localStorage
    localStorage.setItem('saved_username', value);
    // Clear message only when username changes
    if (!rateLimited) {
      setMessage({ text: '', severity: 'info' });
    }
  };

  const handlePasswordChange = (e) => {
    setCredentials({...credentials, password: e.target.value});
    // Don't clear message when typing password
  };

  return (
    <Container 
      maxWidth="xs"  // Changed back from "sm" to "xs" for smaller container
      sx={{ 
        mt: { xs: 4, sm: 6, md: 8, lg: 10 },  // Progressive top margin
        px: { xs: 2, sm: 3 },          
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 3, md: 3.5, lg: 4 },  // Reduced padding
          width: '100%',
          maxWidth: { 
            xs: '320px',   // Mobile (was 360px)
            sm: '360px',   // Tablet (was 420px)
            md: '400px',   // Desktop (was 480px)
            lg: '440px',   // Large desktop (was 540px)
            xl: '480px'    // Extra large screens (was 600px)
          },
          mx: 'auto',
        }}
      >
        {/* LOGO SECTION */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            mb: { xs: 2, sm: 3, md: 3.5 },  // Slightly reduced margin
            width: '100%',
          }}
        >
          {/* Logo Image with Text - Responsive */}
          <Box
            component="img"
            src="/logo.png"
            alt="BalancePro Logo"
            sx={{
              width: {
                xs: '180px',   // Mobile (reduced from 220px)
                sm: '220px',   // Tablet (reduced from 280px)
                md: '260px',   // Desktop (reduced from 340px)
                lg: '300px',   // Large desktop (reduced from 380px)
                xl: '340px'    // Extra large screens (reduced from 420px)
              },
              maxWidth: '85%',  // Back to 85%
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Login Title */}
        <Typography 
          variant="h5"  // Keeping h5 for compact design
          align="center" 
          gutterBottom
          sx={{ 
            fontSize: { 
              xs: '1.4rem',    // Mobile
              sm: '1.6rem',    // Tablet  
              md: '1.8rem',    // Desktop
              lg: '1.9rem'     // Large screens
            },
            mb: { xs: 2, sm: 2.5 },
          }}
        >
          התחברות
        </Typography>
        
        {message.text && (
          <Box sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            mb: { xs: 1.5, sm: 2 }, 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '8px' 
          }}>
            <Typography 
              variant="body1" 
              align="center"
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' } 
              }}
            >
              {message.text}
            </Typography>
          </Box>
        )}
        
        {rateLimited && retryAfter > 0 && (
          <Box sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            mb: { xs: 1.5, sm: 2 }, 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '8px' 
          }}>
            <Typography 
              variant="h5" 
              align="center"
              sx={{ 
                fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' } 
              }}
            >
              {Math.floor(retryAfter / 60)}:{String(retryAfter % 60).padStart(2, '0')}
            </Typography>
          </Box>
        )}
        
        <form onSubmit={handleSubmit}>
          <TextField
            ref={usernameRef}
            name="username"
            label="שם משתמש"
            value={credentials.username}
            fullWidth
            margin="dense"  // Changed to "dense" for more compact form
            onChange={handleUsernameChange}
            required
            disabled={rateLimited}
            autoComplete="username"
            sx={{ 
              direction: 'ltr',
              mb: { xs: 1.5, sm: 2 }
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            ref={passwordRef}
            name="password"
            label="סיסמה"
            value={credentials.password}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="dense"  // Changed to "dense" for more compact form
            onChange={handlePasswordChange}
            required
            disabled={rateLimited}
            autoComplete="current-password"
            sx={{ 
              direction: 'ltr',
              mb: { xs: 1.5, sm: 2 }
            }}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={rateLimited}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button 
            type="submit" 
            fullWidth 
            variant="contained" 
            sx={{ 
              mt: { xs: 2, sm: 2.5 },
              py: { xs: 1.25, sm: 1.5 },  // Reduced button padding
              fontSize: { 
                xs: '0.875rem', 
                sm: '1rem',
                md: '1.05rem'  // Slightly smaller on desktop
              }
            }} 
            disabled={loading || rateLimited}
          >
            {loading ? <CircularProgress size={22} /> : 'התחבר'}
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default LoginPage;